<template>
  <v-container fluid>
    <v-row align="center">
      <v-col class="d-flex flex-row align-center flex-wrap">
        <v-btn text class="mr-4" @click="back"><v-icon>mdi-arrow-left</v-icon></v-btn>
        <div>
          <h1 class="text-h5">{{ tpl ? tpl.name || 'Untitled report' : 'Report' }}</h1>
          <div class="text-caption grey--text">
            {{ state.id ? `Saved report #${state.id} - version ${version}` : 'New report (not saved yet)' }}
            <span v-if="dirty" class="warning--text ml-2">- unsaved changes</span>
          </div>
        </div>
        <v-progress-circular v-if="loading" indeterminate color="green" class="ml-3" />
        <v-spacer />
        <v-btn v-if="state.id" outlined class="mr-2" :to="`/report-hub/${tpl && tpl.key}`" target="_blank"><v-icon left>mdi-open-in-new</v-icon>Open in Hub</v-btn>
        <v-btn outlined class="mr-2" :loading="validating" @click="validate"><v-icon left>mdi-check-circle-outline</v-icon>Check</v-btn>
        <v-btn color="primary" :disabled="!tpl" :loading="saving" @click="saveDialog = true"><v-icon left>mdi-content-save</v-icon>Save</v-btn>
      </v-col>
    </v-row>

    <v-alert v-if="state.problems.length" type="error" outlined dismissible @input="state.problems = []">
      <div class="font-weight-bold mb-1">This report has problems:</div>
      <div v-for="(p, i) in state.problems" :key="i" class="text-body-2">- {{ p }}</div>
    </v-alert>

    <template v-if="tpl">
      <v-tabs v-model="tab" show-arrows class="mb-4">
        <v-tab v-for="t in tabs" :key="t.key"><v-icon left small>{{ t.icon }}</v-icon>{{ t.text }}</v-tab>
      </v-tabs>

      <general-tab v-if="current === 'general'" />
      <filters-tab v-else-if="current === 'filters'" />
      <data-tab v-else-if="current === 'data'" />
      <layout-tab v-else-if="current === 'layout'" />
      <output-tab v-else-if="current === 'output'" />
      <drilldowns-tab v-else-if="current === 'drill'" @goto="goto" />
      <preview-tab v-else-if="current === 'preview'" />
      <tests-tab v-else-if="current === 'tests'" :dirty="dirty" />
      <versions-tab v-else-if="current === 'versions'" :current="version" @loaded="onVersionLoaded" @restored="reload" />
      <json-tab v-else-if="current === 'json'" />
      <ai-tab v-else-if="current === 'ai'" />
    </template>

    <v-dialog v-model="saveDialog" max-width="480">
      <v-card>
        <v-card-title>Save report</v-card-title>
        <v-card-text>
          <v-text-field v-model="saveLabel" outlined dense autofocus label="What changed? (optional note for the version history)" @keyup.enter="save" />
          <div v-if="tpl && tpl.visibility === 'published'" class="text-caption warning--text">This report is published: people will see the new version immediately.</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="saveDialog = false">Cancel</v-btn>
          <v-btn color="primary" :loading="saving" @click="save">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import { errorText } from '../../../components/rb/rbUtil'
import { mapGetters } from 'vuex'
import { state, newTemplate } from '../../../components/rb/builder/editorState'
import GeneralTab from '../../../components/rb/builder/GeneralTab.vue'
import FiltersTab from '../../../components/rb/builder/FiltersTab.vue'
import DataTab from '../../../components/rb/builder/DataTab.vue'
import LayoutTab from '../../../components/rb/builder/LayoutTab.vue'
import OutputTab from '../../../components/rb/builder/OutputTab.vue'
import DrilldownsTab from '../../../components/rb/builder/DrilldownsTab.vue'
import PreviewTab from '../../../components/rb/builder/PreviewTab.vue'
import TestsTab from '../../../components/rb/builder/TestsTab.vue'
import VersionsTab from '../../../components/rb/builder/VersionsTab.vue'
import JsonTab from '../../../components/rb/builder/JsonTab.vue'
import AiTab from '../../../components/rb/builder/AiTab.vue'

const TABS = [
  { key: 'general', text: 'General', icon: 'mdi-information-outline' },
  { key: 'filters', text: 'Filters', icon: 'mdi-filter-variant' },
  { key: 'data', text: 'Data', icon: 'mdi-database-search-outline' },
  { key: 'layout', text: 'Layout', icon: 'mdi-view-dashboard-outline' },
  { key: 'output', text: 'Details, exports & prints', icon: 'mdi-printer-outline' },
  { key: 'drill', text: 'Underlying data', icon: 'mdi-table-search' },
  { key: 'preview', text: 'Preview', icon: 'mdi-eye-outline' },
  { key: 'tests', text: 'Tests', icon: 'mdi-test-tube' },
  { key: 'versions', text: 'Versions', icon: 'mdi-history' },
  { key: 'json', text: 'JSON', icon: 'mdi-code-json' },
  { key: 'ai', text: 'AI', icon: 'mdi-robot-outline' }
]

export default {
  name: 'ReportBuilderEditor',
  components: { GeneralTab, FiltersTab, DataTab, LayoutTab, OutputTab, DrilldownsTab, PreviewTab, TestsTab, VersionsTab, JsonTab, AiTab },
  data () {
    return { state, tabs: TABS, tab: 0, loading: false, saving: false, validating: false, dirty: false, version: 0, saveDialog: false, saveLabel: '', watching: false }
  },
  computed: {
    ...mapGetters(['getEndpoint']),
    base () { return `${this.getEndpoint}/api/rb-builder` },
    tpl () { return state.tpl },
    current () { return TABS[this.tab].key }
  },
  watch: {
    '$route.params.id' () { this.init() },
    // any edit marks the report as changed (after the first load has settled)
    'state.tpl': { deep: true, handler () { if (this.watching) this.dirty = true } }
  },
  async created () {
    window.addEventListener('beforeunload', this.beforeUnload)
    await this.init()
  },
  beforeDestroy () {
    window.removeEventListener('beforeunload', this.beforeUnload)
  },
  beforeRouteLeave (to, from, next) {
    if (this.dirty && !window.confirm('You have unsaved changes. Leave without saving?')) return next(false)
    next()
  },
  methods: {
    beforeUnload (e) {
      if (this.dirty) { e.preventDefault(); e.returnValue = '' }
    },
    back () { this.$router.push('/superadmin/report-builder') },
    goto (tabKey) { this.tab = TABS.findIndex((t) => t.key === tabKey) },
    async init () {
      this.loading = true
      this.watching = false
      try {
        const [meta, list] = await Promise.all([axios.get(`${this.base}/meta`), axios.get(`${this.base}/reports`)])
        state.meta = meta.data.data
        state.categories = list.data.data.categories
        state.ctx = 'main'
        state.problems = []
        state.lastColumns = {}
        state.previewParams = {}
        const id = this.$route.params.id
        if (id) {
          const res = await axios.get(`${this.base}/reports/${id}`)
          state.id = res.data.data.id
          this.version = res.data.data.version
          state.tpl = res.data.data.template
        } else {
          state.id = null
          this.version = 0
          state.tpl = newTemplate()
        }
        if (!state.tpl.permissions) state.tpl.permissions = { userTypeIds: [] }
      } finally {
        this.loading = false
        this.dirty = false
        this.$nextTick(() => { this.watching = true })
      }
    },
    async reload () { this.dirty = false; await this.init() },
    onVersionLoaded (v) {
      this.$snack(`Version ${v} loaded into the editor. Save to keep it.`, 'info')
      this.dirty = true
    },
    async validate () {
      this.validating = true
      try {
        const res = await axios.post(`${this.base}/validate`, { template: state.tpl })
        state.problems = res.data.data.problems
        if (res.data.data.ok) this.$snack('No problems found.', 'success')
      } finally {
        this.validating = false
      }
    },
    async save () {
      this.saving = true
      state.problems = []
      try {
        const body = { template: state.tpl, label: this.saveLabel || undefined }
        if (state.id) {
          const res = await axios.put(`${this.base}/reports/${state.id}`, body)
          this.version = res.data.data.version
          this.$snack(`Saved (version ${this.version}).`, 'success')
        } else {
          const res = await axios.post(`${this.base}/reports`, body)
          state.id = res.data.data.id
          this.version = res.data.data.version
          this.dirty = false
          this.saveDialog = false
          this.$snack('Report created.', 'success')
          this.$router.replace(`/superadmin/report-builder/edit/${state.id}`)
        }
        this.dirty = false
        this.saveDialog = false
        this.saveLabel = ''
      } catch (e) {
        const data = e.response && e.response.data
        state.problems = (data && Array.isArray(data.data) ? data.data : []).filter((x) => typeof x === 'string' && x).slice(0, 12)
        if (!state.problems.length) state.problems = [errorText(e)]
        this.saveDialog = false
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
